import styled from "@emotion/styled";
import { InputLabel, ToggleButton, ToggleButtonGroup, type ToggleButtonGroupProps } from "@mui/material";
import type { ReactNode } from "react";

import { DEFAULT_INPUT_HEIGHT, transientOptions } from "../../utilities";
import type { Option } from "./types";

type ToggleButtonGroupSize = "small" | "default";

const StyledToggleButton = styled(ToggleButton, transientOptions)<{
  $orientation?: "vertical" | "horizontal";
  $size: ToggleButtonGroupSize;
}>`
  flex: 1;

  border: none;
  padding: ${({ $size }) => ($size === "small" ? "0 8px" : "0 10px")};
  // "small" trims the button and surrounding padding so the whole control lands on DEFAULT_INPUT_HEIGHT.
  min-height: ${({ $size }) => ($size === "small" ? DEFAULT_INPUT_HEIGHT - 6 : DEFAULT_INPUT_HEIGHT)}px;

  font-size: inherit;
  text-transform: revert;
  text-align: inherit;

  justify-content: ${({ $orientation }) => ($orientation === "vertical" ? "space-between" : "center")};

  && {
    border-radius: 5px;
  }

  &&.Mui-disabled {
    background: ${({ theme }) => theme.surfaceVariant};
    border: none;
    opacity: 0.8;
  }
`;

const StyledToggleButtonGroup = styled(ToggleButtonGroup, transientOptions)<{ $size: ToggleButtonGroupSize }>`
  background: ${({ theme }) => theme.surface};
  border: solid 1px ${({ theme }) => theme.outlineSoft};
  gap: ${({ $size }) => ($size === "small" ? 2 : 4)}px;
  padding: ${({ $size }) => ($size === "small" ? 2 : 4)}px;
`;

const Label = styled(InputLabel)`
  user-select: none;
`;

export type ToggleButtonGroupControlProps = Pick<ToggleButtonGroupProps, "orientation" | "disabled"> & {
  options: Option[];
  /** Currently-selected option value. */
  value: string;
  /** Called with the newly-selected value (never with the current or a null value). */
  onChange: (value: string) => void;
  label?: ReactNode;
  controlId?: string;
  dataTest?: string;
  /** "small" trims padding to match the default input height; "default" (roomier) is the default. */
  size?: ToggleButtonGroupSize;
};

/**
 * Presentational, controlled toggle button group with the designer styling. Use directly when the
 * value is owned by the caller; {@link FieldToggleButtonGroup} wraps this for Formik-managed values.
 */
export const ToggleButtonGroupControl = ({
  options,
  value,
  onChange,
  label,
  controlId,
  dataTest,
  orientation,
  disabled,
  size = "default",
}: ToggleButtonGroupControlProps) => {
  const handleChange: ToggleButtonGroupProps["onChange"] = (_event, next: string | null) => {
    if (next === null || next === value) return;
    onChange(next);
  };

  return (
    <>
      {label != null && <Label htmlFor={controlId}>{label}</Label>}
      <StyledToggleButtonGroup
        $size={size}
        id={controlId}
        exclusive
        value={value}
        color="primary"
        onChange={handleChange}
        aria-label={typeof label === "string" ? label : undefined}
        data-test={dataTest}
        orientation={orientation}
        disabled={disabled}
      >
        {options.map((option) => (
          <StyledToggleButton
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            $orientation={orientation}
            $size={size}
          >
            {option.label}
          </StyledToggleButton>
        ))}
      </StyledToggleButtonGroup>
    </>
  );
};
